import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Search, Folder, FileText, MessageSquare, Loader2, CornerDownLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlobalSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
    else { setQuery(''); setResults(null); }
  }, [open]);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search`, {
        params: { q, user_role: user?.role || 'instructor' },
      });
      setResults(res.data);
    } catch (e) {
      setResults({ folders: [], documents: [], posts: [] });
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  const onChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 300);
  };

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const highlight = (text) => {
    if (!text || !query.trim()) return text;
    const i = text.toLowerCase().indexOf(query.trim().toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-[#e87722]/25 text-inherit rounded px-0.5">{text.slice(i, i + query.trim().length)}</mark>
        {text.slice(i + query.trim().length)}
      </>
    );
  };

  const total = results ? results.folders.length + results.documents.length + results.posts.length : 0;

  const Row = ({ icon: Icon, iconClass, title, subtitle, snippet, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-[#34657f]/5 rounded-lg transition-colors group"
      data-testid={testId}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${iconClass}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[#1A1A1A] truncate">{highlight(title)}</p>
        {subtitle && <p className="text-xs text-[#5C5C5C] truncate">{subtitle}</p>}
        {snippet && <p className="text-xs text-[#5C5C5C] line-clamp-2 mt-0.5">{highlight(snippet)}</p>}
      </div>
      <CornerDownLeft className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 shrink-0 mt-2" />
    </button>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden sm:flex items-center gap-2 h-11 pl-4 pr-2.5 rounded-full border-2 border-[#34657f]/50 bg-[#f0f5f8] text-sm font-medium text-slate-700 shadow-sm hover:border-[#34657f] hover:bg-white transition-colors w-64 lg:w-96"
        data-testid="global-search-trigger"
      >
        <Search className="w-4.5 h-4.5 shrink-0 text-[#34657f]" strokeWidth={2.5} />
        <span className="flex-1 text-left truncate">Search entire portal…</span>
        <kbd className="text-[10px] font-semibold bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">Ctrl K</kbd>
      </button>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="sm:hidden text-slate-600 hover:text-slate-900"
        aria-label="Search"
        data-testid="global-search-trigger-mobile"
      >
        <Search className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden top-[10%] translate-y-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Search entire portal</DialogTitle>
          <div className="flex items-center gap-3 px-4 border-b border-slate-200">
            {loading
              ? <Loader2 className="w-5 h-5 text-[#34657f] animate-spin shrink-0" />
              : <Search className="w-5 h-5 text-slate-400 shrink-0" />}
            <input
              ref={inputRef}
              value={query}
              onChange={onChange}
              placeholder="Search files, folders, and forum posts…"
              className="w-full h-14 text-base outline-none placeholder:text-slate-400 bg-transparent"
              data-testid="global-search-input"
            />
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {!results && (
              <p className="text-sm text-[#5C5C5C] text-center py-8">
                Type at least 2 characters — searches titles, folder names, forum posts, and the words <em>inside</em> documents.
              </p>
            )}
            {results && total === 0 && !loading && (
              <p className="text-sm text-[#5C5C5C] text-center py-8">No matches for “{query}”.</p>
            )}
            {results?.folders.length > 0 && (
              <div className="mb-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C5C5C] px-4 py-1.5">Folders</p>
                {results.folders.map((f) => (
                  <Row
                    key={f.id}
                    icon={Folder}
                    iconClass="bg-[#b34e00]"
                    title={f.name}
                    onClick={() => go(`/curriculum?${f.section ? `section=${f.section}&` : ''}folder=${f.id}`)}
                    testId={`search-folder-${f.id}`}
                  />
                ))}
              </div>
            )}
            {results?.documents.length > 0 && (
              <div className="mb-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C5C5C] px-4 py-1.5">Documents</p>
                {results.documents.map((d) => (
                  <Row
                    key={d.id}
                    icon={FileText}
                    iconClass="bg-[#34657f]"
                    title={d.title}
                    subtitle={d.folder_name ? `in ${d.folder_name}` : undefined}
                    snippet={d.snippet}
                    onClick={() => go(`/curriculum?folder=${d.folder_id || ''}`)}
                    testId={`search-doc-${d.id}`}
                  />
                ))}
              </div>
            )}
            {results?.posts.length > 0 && (
              <div className="mb-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C5C5C] px-4 py-1.5">Forum Posts</p>
                {results.posts.map((p) => (
                  <Row
                    key={p.id}
                    icon={MessageSquare}
                    iconClass="bg-[#3d7a22]"
                    title={p.title}
                    subtitle={`by ${p.author_name}`}
                    snippet={p.snippet}
                    onClick={() => go(`/forum?thread=${p.thread_id || ''}`)}
                    testId={`search-post-${p.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
